import { useEffect, useState } from "react";
import BlueButton from "./BlueButton";
import { BRAND } from "../css/brand";
import {
  Shuffle,
  X,
  Loader2,
  CheckSquare,
  Square,
  Search,
} from "lucide-react";
import { fetchRoles } from "../Data/ROLES";
import axios from "axios";
import { showPreviewTransferAlert } from "./PreviewTransferAlert";
import Swal from "sweetalert2";

export default function RemapButton({ data, onRemap }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("");
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [newParent, setNewParent] = useState(null); // ✅ store selected parent object
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [parents, setParents] = useState([]); // ✅ list of parents
  const [loadingParents, setLoadingParents] = useState(false);
  const [parentSearch, setParentSearch] = useState("");
  const [showParentList, setShowParentList] = useState(false);

  const company_id = 26;
  const login_user_id = localStorage.getItem("userId");

  // ✅ Fetch roles
  useEffect(() => {
    const loadRoles = async () => {
      const roleMap = await fetchRoles();
      const filteredRoles = Object.entries(roleMap)
        .filter(([id]) => ["4", "5", "7"].includes(id.toString()))
        .map(([, name]) => name);
      setRoles(filteredRoles);
    };
    loadRoles();
  }, []);

  const getRoleId = (type) =>
    type === "Super Stockist" ? 4 : type === "Distributor" ? 5 : 7;

  // ✅ Fetch entities (users)
  useEffect(() => {
    if (!type) return;
    const roleId = getRoleId(type);

    const fetchData = async () => {
      setLoadingUsers(true);
      try {
        let res;
        if (searchTerm.trim()) {
          res = await axios.get(
            `https://spillas.com/api/v1/org_user/search?company_id=${company_id}&login_id=${login_user_id}&role=${roleId}&org_code=${searchTerm}`
          );
          const data = Array.isArray(res.data)
            ? res.data
            : res.data?.data
            ? [res.data.data]
            : [];
          setUsers(data);
        } else {
          res = await axios.get(
            `https://spillas.com/api/v1/org_user?role=${roleId}&company_id=${company_id}&search=&per_page=10000`
          );
          setUsers(res.data?.data || []);
        }
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setLoadingUsers(false);
      }
    };

    const delayDebounce = setTimeout(fetchData, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, type]);

  // ✅ Fetch parent list
  useEffect(() => {
     let roleId;
    if (type === "Retailers") {
      roleId = 5; // Retailer's parent is Distributor
    } else {
      roleId = getRoleId(type) - 1; // Default: one level up
    }

    setLoadingParents(true);
    const fetchParents = async () => {
      try {
        let res;
        if (parentSearch.trim()) {
          res = await axios.get(
            `https://spillas.com/api/v1/org_user/search?company_id=${company_id}&login_id=${login_user_id}&role=${roleId}&org_code=${parentSearch}`
          );
          const data = Array.isArray(res.data)
            ? res.data
            : res.data?.data
            ? [res.data.data]
            : [];
          setParents(data);
        } else {
          res = await axios.get(
            `https://spillas.com/api/v1/org_user?role=${roleId}&company_id=${company_id}&search=&per_page=10000`
          );
          setParents(res.data?.data || []);
        }
      } catch (err) {
        console.error("Error fetching parents:", err);
      } finally {
        setLoadingParents(false);
      }
    };

    const delayDebounce = setTimeout(fetchParents, 500);
    return () => clearTimeout(delayDebounce);
  }, [type, parentSearch]);

  // ✅ Detect click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".parent-search-area")) {
        setShowParentList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Toggle entity selection
  const toggleSelect = (id) => {
    setSelectedEntities((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ✅ Reset all
  const resetAll = () => {
    setType("");
    setSelectedEntities([]);
    setNewParent(null);
    setParentSearch("");
    setSearchTerm("");
    setShowParentList(false);
    setUsers([]);
    setParents([]);
  };

  // ✅ Transfer API call
  const handleTransferAPI = async () => {
    try {
      const payload = {
        from_user_ids: selectedEntities,
        to_user_id: newParent?.id,
        login_user_id: company_id,
      };

      const response = await axios.post(
        "https://spillas.com/api/v1/transfer",
        payload
      );

      if (response) {
        Swal.fire({
          icon: "success",
          title: "Transfer Successful",
          text: "Entities have been remapped successfully!",
        });
        onRemap?.();
        resetAll();
        setOpen(false);
      } else {
        Swal.fire({
          icon: "warning",
          title: "Transfer Failed",
          text: response.data?.message || "Something went wrong.",
        });
      }
    } catch (err) {
      console.error("Error in transfer:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to complete transfer. Please try again.",
      });
    }
  };

  // ✅ Handle Remap click
  const handleRemap = () => {
    if (selectedEntities.length === 0 || !newParent) {
      Swal.fire({
        icon: "warning",
        title: "Missing Data",
        text: "Please select entities and a new parent before proceeding.",
      });
      return;
    }

    const selectedUsers = users.filter((u) => selectedEntities.includes(u.id));

    console.log("Selected parent:", newParent);

    let entityLabel = "";
    let parentRole = "";

    switch (type) {
      case "Super Stockist":
        entityLabel = "Super Stockists";
        parentRole = "CNF";
        break;
      case "Distributor":
        entityLabel = "Distributors";
        parentRole = "Super Stockist";
        break;
      case "Retailer":
        entityLabel = "Retailers";
        parentRole = "Distributor";
        break;
      default:
        entityLabel = "Entities";
        parentRole = "Parent";
        break;
    }

    showPreviewTransferAlert({
      retailers: selectedUsers.map((u) => ({
        orgCode: u.org_code,
        orgName: u.business_name,
        role: type,
      })),
      distributor: {
        orgCode: newParent?.org_code || "",
        orgName: newParent?.business_name || "",
        distName: newParent?.business_name || "",
        role: parentRole,
      },
      title: `Transfer ${entityLabel} to ${parentRole}`,
      onConfirm: handleTransferAPI,
      onCancel: () => console.log("Transfer cancelled."),
    });
  };

  const parentLabel =
    type === "Super Stockist"
      ? "CNF"
      : type === "Distributor"
      ? "Super Stockist"
      : "Distributor";

  return (
    <>
      <BlueButton
        brand={BRAND}
        onClick={() => setOpen(true)}
        icon={<Shuffle size={16} />}
      >
        Remap
      </BlueButton>

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center z-50 pt-20">
          <div className="bg-white rounded-xl shadow-xl p-6 w-[600px] relative">
            <button
              onClick={() => {
                resetAll();
                setOpen(false);
              }}
              className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
            >
              <X size={18} />
            </button>

            <h2 className="text-lg font-semibold mb-4">Remap Entities</h2>

            {/* Entity Type */}
            <div className="mb-3">
              <label className="text-sm text-gray-600 font-medium">
                Entity Type
              </label>
              <select
                className="w-full border rounded-lg p-2 text-sm"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="">Select Type</option>
                {roles.map((r, i) => (
                  <option key={i}>{r}</option>
                ))}
              </select>
            </div>

            {/* Entity List */}
            <div className="mb-3">
              <label className="text-sm text-gray-600 font-medium">
                Select {type || "Entity"}(s)
              </label>
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-2.5 text-gray-400"
                />
                <input
                  type="text"
                  placeholder={`Search ${type || "entity"}`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border pl-9 pr-3 py-2 text-sm"
                />
              </div>

              <div className="max-h-40 overflow-y-auto border rounded-lg mt-2">
                {loadingUsers ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 p-2">
                    <Loader2 size={16} className="animate-spin" /> Loading...
                  </div>
                ) : users.length === 0 ? (
                  <p className="text-sm text-gray-500 p-2">
                    No {type || "entities"} found
                  </p>
                ) : (
                  users.map((u) => (
                    <div
                      key={u.id}
                      onClick={() => toggleSelect(u.id)}
                      className={`flex items-center justify-between px-3 py-1.5 text-sm border-b cursor-pointer ${
                        selectedEntities.includes(u.id)
                          ? "bg-blue-50 text-blue-700"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <div>
                        <p className="font-medium">
                          {u.org_code} {u.business_name}
                        </p>
                        <p className="text-xs text-gray-500">{u.id}</p>
                      </div>
                      {selectedEntities.includes(u.id) ? (
                        <CheckSquare size={16} />
                      ) : (
                        <Square size={16} />
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Selected IDs */}
              {selectedEntities.length > 0 && (
                <div className="mt-2 text-xs text-gray-700 bg-gray-50 border rounded-lg p-2">
                  <span className="font-medium">Selected IDs:</span>{" "}
                  {selectedEntities.join(", ")}
                </div>
              )}
            </div>

            {/* Parent Selection */}
            <div className="parent-search-area mb-4 relative">
              <label className="text-sm text-gray-600 font-medium">
                New {parentLabel}
              </label>
              <div className="relative mt-1">
                <Search
                  size={16}
                  className="absolute left-3 top-2.5 text-gray-400"
                />
                <input
                  type="text"
                  placeholder={`Search ${parentLabel}`}
                  value={
                    newParent
                      ? `${newParent.org_code} ${newParent.business_name} (${newParent.id})`
                      : parentSearch
                  }
                  onFocus={() => setShowParentList(true)}
                  onChange={(e) => {
                    setParentSearch(e.target.value);
                    setShowParentList(true);
                    setNewParent(null);
                  }}
                  className="w-full rounded-lg border pl-9 pr-3 py-2 text-sm"
                />
                {showParentList && (
                  <div className="absolute z-10 bg-white border rounded-lg mt-1 w-full max-h-52 overflow-y-auto shadow-lg">
                    {loadingParents ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 p-2">
                        <Loader2 size={16} className="animate-spin" /> Loading...
                      </div>
                    ) : parents.length === 0 ? (
                      <p className="text-sm text-gray-500 p-2">
                        No {parentLabel}s found
                      </p>
                    ) : (
                      parents.map((p) => (
                        <div
                          key={p.id}
                          onClick={() => {
                            setNewParent(p); // ✅ fix
                            setParentSearch("");
                            setShowParentList(false);
                          }}
                          className={`px-3 py-2 border-b cursor-pointer text-sm ${
                            newParent?.id === p.id
                              ? "bg-blue-100 text-blue-700"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          {p.org_code} {p.business_name} ({p.id})
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  resetAll();
                  setOpen(false);
                }}
                className="px-4 py-2 text-sm rounded-lg border text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <BlueButton
                brand={BRAND}
                onClick={handleRemap}
                disabled={
                  selectedEntities.length === 0 || !newParent || !type
                }
              >
                Confirm Remap
              </BlueButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



// import { useEffect, useState } from "react";
// import BlueButton from "./BlueButton";
// import { BRAND } from "../css/brand";
// import {
//   Shuffle,
//   X,
//   Loader2,
//   CheckSquare,
//   Square,
//   Search,
// } from "lucide-react";
// import { fetchRoles } from "../Data/ROLES";
// import axios from "axios";
// import { showPreviewTransferAlert } from "./PreviewTransferAlert"; // ✅ Import SweetAlert preview
// import Swal from "sweetalert2";

// export default function RemapButton({ data, onRemap }) {
//   const [open, setOpen] = useState(false);
//   const [type, setType] = useState("");
//   const [selectedEntities, setSelectedEntities] = useState([]);
//   const [newParent, setNewParent] = useState();
//   const [roles, setRoles] = useState([]);
//   const [users, setUsers] = useState([]);
//   const [loadingUsers, setLoadingUsers] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");

//   const [parents, setParents] = useState([]);
//   console.log("selected parent:", parents)
//   const [loadingParents, setLoadingParents] = useState(false);
//   const [parentSearch, setParentSearch] = useState("");
//   const [showParentList, setShowParentList] = useState(false);

//   const company_id = 26;
//   const login_user_id = localStorage.getItem("userId"); // ✅ session-based user ID

//   // ✅ Fetch roles
//   useEffect(() => {
//     const loadRoles = async () => {
//       const roleMap = await fetchRoles();
//       const filteredRoles = Object.entries(roleMap)
//         .filter(([id]) => ["4", "5", "7"].includes(id.toString()))
//         .map(([, name]) => name);
//       setRoles(filteredRoles);
//     };
//     loadRoles();
//   }, []);

//   // ✅ Get Role ID based on Type
//   const getRoleId = (type) =>
//     type === "Super Stockist" ? 4 : type === "Distributor" ? 5 : 7;

//   // ✅ Fetch entities (users)
//   // ✅ Fetch entities (users)
// useEffect(() => {
//   if (!type) return;
//   const roleId = getRoleId(type);

//   const fetchData = async () => {
//     setLoadingUsers(true);
//     try {
//       let res;
//       if (searchTerm.trim()) {
//         // 🔍 Use search API when searching
//         res = await axios.get(
//           `https://spillas.com/api/v1/org_user/search?company_id=${company_id}&login_id=${login_user_id}&role=${roleId}&org_code=${searchTerm}`
//         );
//         // API returns a single match or array — normalize to array
//         const data = Array.isArray(res.data)
//           ? res.data
//           : res.data?.data
//           ? [res.data.data]
//           : [];
//         setUsers(data);
//       } else {
//         // 📋 Use main listing API when no search
//         res = await axios.get(
//           `https://spillas.com/api/v1/org_user?role=${roleId}&company_id=${company_id}&search=&per_page=10000`
//         );
//         setUsers(res.data?.data || []);
//       }
//     } catch (err) {
//       console.error("Error fetching users:", err);
//     } finally {
//       setLoadingUsers(false);
//     }
//   };

//   const delayDebounce = setTimeout(fetchData, 500);
//   return () => clearTimeout(delayDebounce);
// }, [searchTerm, type]);


//   // ✅ Fetch parent list
//   // ✅ Fetch parent list
// useEffect(() => {
//   if (!type) return;
//   const roleId = getRoleId(type) - 1; // parent role
//   setLoadingParents(true);

//   const fetchParents = async () => {
//     try {
//       let res;
//       if (parentSearch.trim()) {
//         // 🔍 Use search API for parent search
//         res = await axios.get(
//           `https://spillas.com/api/v1/org_user/search?company_id=${company_id}&login_id=${login_user_id}&role=${roleId}&org_code=${parentSearch}`
//         );
//         const data = Array.isArray(res.data)
//           ? res.data
//           : res.data?.data
//           ? [res.data.data]
//           : [];
//         setParents(data);
//       } else {
//         // 📋 Default list
//         res = await axios.get(
//           `https://spillas.com/api/v1/org_user?role=${roleId}&company_id=${company_id}&search=&per_page=10000`
//         );
//         setParents(res.data?.data || []);
//       }
//     } catch (err) {
//       console.error("Error fetching parents:", err);
//     } finally {
//       setLoadingParents(false);
//     }
//   };

//   const delayDebounce = setTimeout(fetchParents, 500);
//   return () => clearTimeout(delayDebounce);
// }, [type, parentSearch]);


//   // ✅ Close parent dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (!e.target.closest(".parent-search-area")) {
//         setShowParentList(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   // ✅ Toggle entity selection
//   const toggleSelect = (id) => {
//     setSelectedEntities((prev) =>
//       prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
//     );
//   };

//   // ✅ Reset all
//   const resetAll = () => {
//     setType("");
//     setSelectedEntities([]);
//     setNewParent("");
//     setParentSearch("");
//     setSearchTerm("");
//     setShowParentList(false);
//     setUsers([]);
//     setParents([]);
//   };

//   // ✅ API call for transfer after confirmation
//   const handleTransferAPI = async () => {
//     try {
//       const payload = {
//         from_user_ids: selectedEntities,
//         to_user_id: newParent,
//         login_user_id : company_id,
//       };

//       const response = await axios.post(
//         "https://spillas.com/api/v1/transfer",
//         payload
//       );

//       if (response) {
//         Swal.fire({
//           icon: "success",
//           title: "Transfer Successful",
//           text: "Entities have been remapped successfully!",
//         });
//         onRemap?.();
//         resetAll();
//         setOpen(false);
//       } else {
//         Swal.fire({
//           icon: "warning",
//           title: "Transfer Failed",
//           text: response.data?.message || "Something went wrong.",
//         });
//       }
//     } catch (err) {
//       console.error("Error in transfer:", err);
//       Swal.fire({
//         icon: "error",
//         title: "Error",
//         text: "Unable to complete transfer. Please try again.",
//       });
//     }
//   };

//   // ✅ Handle Remap click → show preview modal
//   const handleRemap = () => {
//   if (selectedEntities.length === 0 || !parents) {
//     Swal.fire({
//       icon: "warning",
//       title: "Missing Data",
//       text: "Please select entities and a new parent before proceeding.",
//     });
//     return;
//   }

//   // Find selected entities (based on type)
//   const selectedUsers = users.filter((u) => selectedEntities.includes(u.id));

//   // Find selected parent
//   const parentUser = parents.find(
//     (p) => p.id === parents
//   );
//    console.log("All parents:", parents);
//    console.log("selected parent user: ", parentUser) // Determine titles based on entity type
//   let entityLabel = "";
//   let parentRole = "";

//   switch (type) {
//     case "Super Stockist":
//       entityLabel = "Super Stockists";
//       parentRole = "CNF";
//       break;
//     case "Distributor":
//       entityLabel = "Distributors";
//       parentRole = "Super Stockist";
//       break;
//     case "Retailer":
//       entityLabel = "Retailers";
//       parentRole = "Distributor";
//       break;
//     default:
//       entityLabel = "Entities";
//       parentRole = "Parent";
//       break;
//   }

//   // Prepare preview data
//   showPreviewTransferAlert({
//     retailers: selectedUsers.map((u) => ({
//       orgCode: u.org_code,
//       orgName: u.business_name,
//       role: type, // shows correct role on each card
//     })),
//     distributor:{
//       orgCode: parentUser?.org_code || "",
//       orgName: parentUser?.business_name || "",
//       distName: parentUser?.business_name || "",
//       role: parentRole,
//     },
//     title: `Transfer ${entityLabel} to ${parentRole}`,
//     onConfirm: handleTransferAPI,
//     onCancel: () => console.log("Transfer cancelled."),
//   });
// };


//   const parentLabel =
//     type === "Super Stockist"
//       ? "CNF"
//       : type === "Distributor"
//       ? "Super Stockist"
//       : "Distributor";

//   return (
//     <>
//       <BlueButton
//         brand={BRAND}
//         onClick={() => setOpen(true)}
//         icon={<Shuffle size={16} />}
//       >
//         Remap
//       </BlueButton>

//       {open && (
//         <div className="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center z-50 pt-20">
//           <div className="bg-white rounded-xl shadow-xl p-6 w-[600px] relative">
//             <button
//               onClick={() => {
//                 resetAll();
//                 setOpen(false);
//               }}
//               className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
//             >
//               <X size={18} />
//             </button>

//             <h2 className="text-lg font-semibold mb-4">Remap Entities</h2>

//             {/* Entity Type */}
//             <div className="mb-3">
//               <label className="text-sm text-gray-600 font-medium">
//                 Entity Type
//               </label>
//               <select
//                 className="w-full border rounded-lg p-2 text-sm"
//                 value={type}
//                 onChange={(e) => setType(e.target.value)}
//               >
//                 <option value="">Select Type</option>
//                 {roles.map((r, i) => (
//                   <option key={i}>{r}</option>
//                 ))}
//               </select>
//             </div>

//             {/* Entity List */}
//             <div className="mb-3">
//               <label className="text-sm text-gray-600 font-medium">
//                 Select {type || "Entity"}(s)
//               </label>
//               <div className="relative">
//                 <Search
//                   size={16}
//                   className="absolute left-3 top-2.5 text-gray-400"
//                 />
//                 <input
//                   type="text"
//                   placeholder={`Search ${type || "entity"}`}
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                   className="w-full rounded-lg border pl-9 pr-3 py-2 text-sm"
//                 />
//               </div>

//               <div className="max-h-40 overflow-y-auto border rounded-lg mt-2">
//                 {loadingUsers ? (
//                   <div className="flex items-center gap-2 text-sm text-gray-500 p-2">
//                     <Loader2 size={16} className="animate-spin" /> Loading...
//                   </div>
//                 ) : users.length === 0 ? (
//                   <p className="text-sm text-gray-500 p-2">
//                     No {type || "entities"} found
//                   </p>
//                 ) : (
//                   users.map((u) => (
//                     <div
//                       key={u.id}
//                       onClick={() => toggleSelect(u.id)}
//                       className={`flex items-center justify-between px-3 py-1.5 text-sm border-b cursor-pointer ${
//                         selectedEntities.includes(u.id)
//                           ? "bg-blue-50 text-blue-700"
//                           : "hover:bg-gray-50"
//                       }`}
//                     >
//                       <div>
//                         <p className="font-medium">
//                           {u.org_code} {u.business_name}
//                         </p>
//                         <p className="text-xs text-gray-500">{u.id}</p>
//                       </div>
//                       {selectedEntities.includes(u.id) ? (
//                         <CheckSquare size={16} />
//                       ) : (
//                         <Square size={16} />
//                       )}
//                     </div>
//                   ))
//                 )}
//               </div>

//               {/* ✅ Show Selected IDs */}
//               {selectedEntities.length > 0 && (
//                 <div className="mt-2 text-xs text-gray-700 bg-gray-50 border rounded-lg p-2">
//                   <span className="font-medium">Selected IDs:</span>{" "}
//                   {selectedEntities.join(", ")}
//                 </div>
//               )}
//             </div>


//             {/* Parent Selection */}
//             <div className="parent-search-area mb-4 relative">
//               <label className="text-sm text-gray-600 font-medium">
//                 New {parentLabel}
//               </label>
//               <div className="relative mt-1">
//                 <Search
//                   size={16}
//                   className="absolute left-3 top-2.5 text-gray-400"
//                 />
//                 <input
//                   type="text"
//                   placeholder={`Search ${parentLabel}`}
//                   value={parentSearch}
//                   onFocus={() => setShowParentList(true)}
//                   onChange={(e) => {
//                     setParentSearch(e.target.value);
//                     setShowParentList(true);
//                   }}
//                   className="w-full rounded-lg border pl-9 pr-3 py-2 text-sm"
//                 />
//                 {showParentList && (
//                   <div className="absolute z-10 bg-white border rounded-lg mt-1 w-full max-h-52 overflow-y-auto shadow-lg">
//                     {loadingParents ? (
//                       <div className="flex items-center gap-2 text-sm text-gray-500 p-2">
//                         <Loader2 size={16} className="animate-spin" /> Loading...
//                       </div>
//                     ) : parents.length === 0 ? (
//                       <p className="text-sm text-gray-500 p-2">
//                         No {parentLabel}s found
//                       </p>
//                     ) : (
//                       parents.map((p) => (
//                         <div
//                           key={p.id}
//                           onClick={() => {
//                             setParents(p);
//                             setParentSearch(
//                               `${p.org_code} ${p.business_name} (${p.id})`
//                             );
//                             setShowParentList(false);
//                           }}
//                           className={`px-3 py-2 border-b cursor-pointer text-sm ${
//                             newParent === p.id.toString()
//                               ? "bg-blue-100 text-blue-700"
//                               : "hover:bg-gray-50"
//                           }`}
//                         >
//                           {p.org_code} {p.business_name} ({p.id})
//                         </div>
//                       ))
//                     )}
//                   </div>
//                 )}
//               </div>
//             </div>

//             {/* Buttons */}
//             <div className="flex justify-end gap-3">
//               <button
//                 onClick={() => {
//                   resetAll();
//                   setOpen(false);
//                 }}
//                 className="px-4 py-2 text-sm rounded-lg border text-gray-600 hover:bg-gray-100"
//               >
//                 Cancel
//               </button>

//               <BlueButton
//                 brand={BRAND}
//                 onClick={handleRemap}
//                 disabled={selectedEntities.length === 0 || !newParent || !type}
//               >
//                 Confirm Remap
//               </BlueButton>
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// }




// import { useEffect, useState } from "react";
// import BlueButton from "./BlueButton";
// import { BRAND } from "../css/brand";
// import {
//   Shuffle,
//   X,
//   Loader2,
//   CheckSquare,
//   Square,
//   Search,
// } from "lucide-react";
// import { fetchRoles } from "../Data/ROLES";
// import axios from "axios";

// export default function RemapButton({ data, onRemap }) {
//   const [open, setOpen] = useState(false);
//   const [type, setType] = useState("");
//   const [selectedEntities, setSelectedEntities] = useState([]);
//   const [newParent, setNewParent] = useState("");
//   const [roles, setRoles] = useState([]);
//   const [users, setUsers] = useState([]);
//   const [parents, setParents] = useState([]);
//   const [loadingUsers, setLoadingUsers] = useState(false);
//   const [loadingParents, setLoadingParents] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [parentSearch, setParentSearch] = useState("");

//   const company_id = 26; // Replace dynamically if needed

//   // ✅ Fetch roles
//   useEffect(() => {
//     const loadRoles = async () => {
//       const roleMap = await fetchRoles();
//       const filteredRoles = Object.entries(roleMap)
//         .filter(([id]) => ["4", "5", "7"].includes(id.toString()))
//         .map(([, name]) => name);
//       setRoles(filteredRoles);
//     };
//     loadRoles();
//   }, []);

//   // ✅ Determine role ID helper
//   const getRoleId = (t) =>
//     t === "Super Stockist" ? 4 : t === "Distributor" ? 5 : 7;

//   // ✅ Determine parent role ID
//   const getParentRoleId = (t) =>
//     t === "Super Stockist" ? 3 : t === "Distributor" ? 4 : 5;

//   // ✅ Fetch entities and parents when type changes
//   useEffect(() => {
//     if (!type) return;

//     const roleId = getRoleId(type);
//     const parentRoleId = getParentRoleId(type);

//     const fetchUsers = async () => {
//       setLoadingUsers(true);
//       try {
//         const res = await axios.get(
//           `https://spillas.com/api/v1/org_user?role=${roleId}&company_id=${company_id}&page=1&per_page=10000`
//         );
//         setUsers(res.data?.data || []);
//       } catch (err) {
//         console.error("Error fetching users:", err);
//         setUsers([]);
//       } finally {
//         setLoadingUsers(false);
//       }
//     };

//     const fetchParents = async () => {
//       setLoadingParents(true);
//       try {
//         const res = await axios.get(
//           `https://spillas.com/api/v1/org_user?role=${parentRoleId}&company_id=${company_id}&page=1&per_page=10000`
//         );
//         setParents(res.data?.data || []);
//       } catch (err) {
//         console.error("Error fetching parents:", err);
//         setParents([]);
//       } finally {
//         setLoadingParents(false);
//       }
//     };

//     setSelectedEntities([]);
//     setNewParent("");
//     setSearchTerm("");
//     setParentSearch("");
//     fetchUsers();
//     fetchParents();
//   }, [type]);

//   // ✅ Filter users based on search
//   const filteredUsers = users.filter((u) => {
//     const term = searchTerm.toLowerCase();
//     return (
//       u.business_name?.toLowerCase().includes(term) ||
//       u.org_code?.toLowerCase().includes(term) ||
//       u.id?.toString().includes(term)
//     );
//   });

//   // ✅ Filter parents based on search
//   const filteredParents = parents.filter((p) => {
//     const term = parentSearch.toLowerCase();
//     return (
//       p.business_name?.toLowerCase().includes(term) ||
//       p.org_code?.toLowerCase().includes(term) ||
//       p.id?.toString().includes(term)
//     );
//   });

//   // ✅ Toggle selection
//   const handleEntityClick = (id) => {
//     setSelectedEntities((prev) =>
//       prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
//     );
//   };

//   // ✅ Apply Remap
//   const handleApply = () => {
//     if (selectedEntities.length > 0 && newParent) {
//       onRemap(type, selectedEntities, newParent);
//       setOpen(false);
//     }
//   };

//   // ✅ Get label for parent dropdown
//   const parentLabel =
//     type === "Super Stockist"
//       ? "CNF"
//       : type === "Distributor"
//       ? "Super Stockist"
//       : "Distributor";

//   return (
//     <div>
//       {/* Trigger button */}
//       <BlueButton brand={BRAND} onClick={() => setOpen(true)}>
//         <Shuffle size={14} className="inline mr-1" /> Remap
//       </BlueButton>

//       {/* Modal */}
//       {open && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
//           <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-lg p-6 relative">
//             {/* Header */}
//             <div className="flex items-center justify-between mb-4">
//               <h2 className="text-lg font-semibold text-gray-800">
//                 Remap Entities
//               </h2>
//               <button
//                 onClick={() => setOpen(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 <X size={20} />
//               </button>
//             </div>

//             {/* Form */}
//             <div className="space-y-3">
//               {/* Type */}
//               <div>
//                 <label className="text-sm text-gray-600 font-medium">
//                   Select Type
//                 </label>
//                 <select
//                   value={type}
//                   onChange={(e) => setType(e.target.value)}
//                   className="w-full mt-1 rounded-lg border px-3 py-2 text-sm"
//                 >
//                   <option value="">Select Type</option>
//                   {roles.map((roleName, idx) => (
//                     <option key={idx} value={roleName}>
//                       {roleName}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               {/* Entities List */}
//               <div>
//                 <label className="text-sm text-gray-600 font-medium">
//                   Select {type || "Entities"}
//                 </label>

//                 {/* 🔍 Search Bar */}
//                 <div className="relative mt-1">
//                   <Search
//                     size={16}
//                     className="absolute left-3 top-2.5 text-gray-400"
//                   />
//                   <input
//                     type="text"
//                     placeholder={`Search ${type} by name, code or ID`}
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                     className="w-full rounded-lg border pl-9 pr-3 py-2 text-sm"
//                   />
//                 </div>

//                 {loadingUsers ? (
//                   <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
//                     <Loader2 size={16} className="animate-spin" /> Loading...
//                   </div>
//                 ) : (
//                   <div className="mt-2 border rounded-lg max-h-52 overflow-y-auto">
//                     {filteredUsers.length === 0 ? (
//                       <p className="text-sm text-gray-500 p-2">
//                         No users found
//                       </p>
//                     ) : (
//                       filteredUsers.map((u) => {
//                         const selected = selectedEntities.includes(
//                           u.id.toString()
//                         );
//                         return (
//                           <div
//                             key={u.id}
//                             onClick={() => handleEntityClick(u.id.toString())}
//                             className={`flex items-center justify-between px-3 py-2 cursor-pointer border-b text-sm ${
//                               selected
//                                 ? "bg-blue-100 text-blue-700"
//                                 : "hover:bg-gray-50"
//                             }`}
//                           >
//                             <span>
//                               {u.org_code} {u.business_name} ({u.id})
//                             </span>
//                             {selected ? (
//                               <CheckSquare size={16} />
//                             ) : (
//                               <Square size={16} />
//                             )}
//                           </div>
//                         );
//                       })
//                     )}
//                   </div>
//                 )}
//               </div>

//               {/* Selected IDs */}
//               {selectedEntities.length > 0 && (
//                 <div>
//                   <label className="text-sm text-gray-600 font-medium">
//                     Selected User IDs
//                   </label>
//                   <input
//                     type="text"
//                     readOnly
//                     value={selectedEntities.join(", ")}
//                     className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-gray-50 text-gray-700"
//                   />
//                 </div>
//               )}

//               {/* New Parent */}
//               <div>
//                 <label className="text-sm text-gray-600 font-medium">
//                   New {parentLabel}
//                 </label>

//                 {/* 🔍 Parent Search Bar */}
//                 <div className="relative mt-1">
//                   <Search
//                     size={16}
//                     className="absolute left-3 top-2.5 text-gray-400"
//                   />
//                   <input
//                     type="text"
//                     placeholder={`Search ${parentLabel} by name, code or ID`}
//                     value={parentSearch}
//                     onChange={(e) => setParentSearch(e.target.value)}
//                     className="w-full rounded-lg border pl-9 pr-3 py-2 text-sm"
//                   />
//                 </div>

//                 {loadingParents ? (
//                   <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
//                     <Loader2 size={16} className="animate-spin" /> Loading...
//                   </div>
//                 ) : (
//                   <div className="mt-2 border rounded-lg max-h-52 overflow-y-auto">
//                     {filteredParents.length === 0 ? (
//                       <p className="text-sm text-gray-500 p-2">
//                         No {parentLabel}s found
//                       </p>
//                     ) : (
//                       filteredParents.map((p) => (
//                         <div
//                           key={p.id}
//                           onClick={() => setNewParent(p.id.toString())}
//                           className={`px-3 py-2 border-b cursor-pointer text-sm ${
//                             newParent === p.id.toString()
//                               ? "bg-blue-100 text-blue-700"
//                               : "hover:bg-gray-50"
//                           }`}
//                         >
//                           {p.org_code} {p.business_name} ({p.id})
//                         </div>
//                       ))
//                     )}
//                   </div>
//                 )}
//               </div>
//             </div>

//             {/* Footer */}
//             <div className="flex justify-end gap-3 mt-6">
//               <button
//                 onClick={() => setOpen(false)}
//                 className="px-4 py-2 text-sm rounded-lg border text-gray-600 hover:bg-gray-100"
//               >
//                 Cancel
//               </button>
//               <BlueButton
//                 brand={BRAND}
//                 onClick={handleApply}
//                 disabled={selectedEntities.length === 0 || !newParent}
//               >
//                 Apply
//               </BlueButton>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }


// import { useState } from "react";
// import BlueButton from "./BlueButton";
// import { BRAND } from "../css/brand";
// import { Shuffle, X } from "lucide-react";

// export default function RemapButton({ data, onRemap }) {
//   const [open, setOpen] = useState(false);
//   const [type, setType] = useState("Distributor");
//   const [entity, setEntity] = useState("");
//   const [newParent, setNewParent] = useState("");

//   // ✅ Safely handle undefined arrays
//   const sstks = data?.sstks || [];
//   const dists = data?.dists || [];
//   const mdists = data?.mdists || [];
//   const rets = data?.rets || [];
//   const cnfs = data?.cnfs || [];

//   const pool =
//     type === "Super Stockist"
//       ? sstks
//       : type === "Distributor"
//       ? dists
//       : rets;

//   const parents =
//     type === "Super Stockist"
//       ? cnfs
//       : type === "Distributor"
//       ? sstks
//       : [...dists, ...mdists];

//   const parentLabel =
//     type === "Super Stockist"
//       ? "CNF"
//       : type === "Distributor"
//       ? "Super Stockist"
//       : "Distributor/Micro";

//   const handleApply = () => {
//     if (entity && newParent) {
//       onRemap(type, entity, newParent);
//       setOpen(false);
//     }
//   };

//   return (
//     <div>
//       {/* Trigger button */}
//       <BlueButton brand={BRAND} onClick={() => setOpen(true)}>
//         <Shuffle size={14} className="inline mr-1" /> Remap
//       </BlueButton>

//       {/* Modal */}
//       {open && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
//           <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-lg p-6 relative">
//             {/* Header */}
//             <div className="flex items-center justify-between mb-4">
//               <h2 className="text-lg font-semibold text-gray-800">
//                 Remap Entities
//               </h2>
//               <button
//                 onClick={() => setOpen(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 <X size={20} />
//               </button>
//             </div>

//             {/* Form */}
//             <div className="space-y-3">
//               <div>
//                 <label className="text-sm text-gray-600 font-medium">
//                   Select Type
//                 </label>
//                 <select
//                   value={type}
//                   onChange={(e) => {
//                     setType(e.target.value);
//                     setEntity("");
//                     setNewParent("");
//                   }}
//                   className="w-full mt-1 rounded-lg border px-3 py-2 text-sm"
//                 >
//                   <option>Super Stockist</option>
//                   <option>Distributor</option>
//                   <option>Retailer</option>
//                 </select>
//               </div>

//               <div>
//                 <label className="text-sm text-gray-600 font-medium">
//                   Select {type}
//                 </label>
//                 <select
//                   value={entity}
//                   onChange={(e) => setEntity(e.target.value)}
//                   className="w-full mt-1 rounded-lg border px-3 py-2 text-sm"
//                 >
//                   <option value="">Select {type}</option>
//                   {pool.map((p) => (
//                     <option key={p.id} value={p.id}>
//                       {p.name} ({p.id})
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               <div>
//                 <label className="text-sm text-gray-600 font-medium">
//                   New {parentLabel}
//                 </label>
//                 <select
//                   value={newParent}
//                   onChange={(e) => setNewParent(e.target.value)}
//                   className="w-full mt-1 rounded-lg border px-3 py-2 text-sm"
//                 >
//                   <option value="">New {parentLabel}</option>
//                   {parents.map((p) => (
//                     <option key={p.id} value={p.id}>
//                       {p.name} ({p.id})
//                     </option>
//                   ))}
//                 </select>
//               </div>
//             </div>

//             {/* Footer buttons */}
//             <div className="flex justify-end gap-3 mt-6">
//               <button
//                 onClick={() => setOpen(false)}
//                 className="px-4 py-2 text-sm rounded-lg border text-gray-600 hover:bg-gray-100"
//               >
//                 Cancel
//               </button>
//               <BlueButton brand={BRAND} onClick={handleApply}>
//                 Apply
//               </BlueButton>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }


// import { useState } from "react";
// import BlueButton from "./BlueButton";
// import { BRAND } from "../css/brand";
// import { Shuffle } from "lucide-react";

// export default function RemapButton({ data, onRemap }) {
//   const [open, setOpen] = useState(false);
//   const [type, setType] = useState("Distributor");
//   const [entity, setEntity] = useState("");
//   const [newParent, setNewParent] = useState("");

//   // ✅ Safely handle undefined arrays
//   const sstks = data?.sstks || [];
//   const dists = data?.dists || [];
//   const mdists = data?.mdists || [];
//   const rets = data?.rets || [];
//   const cnfs = data?.cnfs || [];

//   const pool =
//     type === "Super Stockist"
//       ? sstks
//       : type === "Distributor"
//       ? dists
//       : rets;

//   const parents =
//     type === "Super Stockist"
//       ? cnfs
//       : type === "Distributor"
//       ? sstks
//       : [...dists, ...mdists];

//   const parentLabel =
//     type === "Super Stockist"
//       ? "CNF"
//       : type === "Distributor"
//       ? "Super Stockist"
//       : "Distributor/Micro";

//   return (
//     <div>
//       <BlueButton brand={BRAND} onClick={() => setOpen(true)}>
//         <Shuffle size={14} className="inline mr-1" /> Remap
//       </BlueButton>

//       {open && (
//         <div className="mt-2 rounded-2xl border bg-white p-4 shadow-xl">
//           <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
//             <select
//               value={type}
//               onChange={(e) => {
//                 setType(e.target.value);
//                 setEntity("");
//                 setNewParent("");
//               }}
//               className="rounded-lg border px-2 py-2 text-sm"
//             >
//               <option>Super Stockist</option>
//               <option>Distributor</option>
//               <option>Retailer</option>
//             </select>

//             <select
//               value={entity}
//               onChange={(e) => setEntity(e.target.value)}
//               className="rounded-lg border px-2 py-2 text-sm sm:col-span-2"
//             >
//               <option value="">Select {type}</option>
//               {pool.map((p) => (
//                 <option key={p.id} value={p.id}>
//                   {p.name} ({p.id})
//                 </option>
//               ))}
//             </select>

//             <select
//               value={newParent}
//               onChange={(e) => setNewParent(e.target.value)}
//               className="rounded-lg border px-2 py-2 text-sm"
//             >
//               <option value="">New {parentLabel}</option>
//               {parents.map((p) => (
//                 <option key={p.id} value={p.id}>
//                   {p.name} ({p.id})
//                 </option>
//               ))}
//             </select>

//             <div className="flex items-center justify-end">
//               <BlueButton
//                 brand={BRAND}
//                 onClick={() => {
//                   if (entity && newParent) {
//                     onRemap(type, entity, newParent);
//                     setOpen(false);
//                   }
//                 }}
//               >
//                 Apply
//               </BlueButton>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }


// // import { useState } from "react";
// // import BlueButton from "./BlueButton";
// // import { BRAND } from "../css/brand";
// // import { Shuffle } from "lucide-react";

// // export default function RemapButton({ data, onRemap }) {
// //   const [open, setOpen] = useState(false);
// //   const [type, setType] = useState("Distributor");
// //   const [entity, setEntity] = useState("");
// //   const [newParent, setNewParent] = useState("");
// //   const pool = type === "Super Stockist" ? data.sstks : type === "Distributor" ? data.dists : data.rets;
// //   const parents = type === "Super Stockist" ? data.cnfs : type === "Distributor" ? data.sstks : [...data.dists, ...data.mdists];
// //   const parentLabel = type === "Super Stockist" ? "CNF" : type === "Distributor" ? "Super Stockist" : "Distributor/Micro";
// //   return (
// //     <div>
// //       <BlueButton brand={BRAND} onClick={() => setOpen(true)}><Shuffle size={14} className="inline mr-1" />Remap</BlueButton>
// //       {open && (
// //         <div className="mt-2 rounded-2xl border bg-white p-4 shadow-xl">
// //           <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
// //             <select value={type} onChange={(e) => { setType(e.target.value); setEntity(""); setNewParent(""); }} className="rounded-lg border px-2 py-2 text-sm">
// //               <option>Super Stockist</option>
// //               <option>Distributor</option>
// //               <option>Retailer</option>
// //             </select>
// //             <select value={entity} onChange={(e) => setEntity(e.target.value)} className="rounded-lg border px-2 py-2 text-sm sm:col-span-2">
// //               <option value="">Select {type}</option>
// //               {pool.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.id})</option>)}
// //             </select>
// //             <select value={newParent} onChange={(e) => setNewParent(e.target.value)} className="rounded-lg border px-2 py-2 text-sm">
// //               <option value="">New {parentLabel}</option>
// //               {parents.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.id})</option>)}
// //             </select>
// //             <div className="flex items-center justify-end">
// //               <BlueButton brand={BRAND} onClick={() => { if (entity && newParent) { onRemap(type, entity, newParent); setOpen(false); } }}>Apply</BlueButton>
// //             </div>
// //           </div>
// //         </div>
// //       )}
// //     </div>
// //   );
// // }
